import React from 'react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { AlertTriangle } from 'lucide-react';
import { Layers } from 'lucide-react';

type RaptorSection = 'fbe' | 'cmpp';
type RaptorView = 'dashboard' | 'grid' | 'table' | 'warnings' | 'danger';

interface RaptorCoatingControlDashboardProps {
    section: RaptorSection;
    apiResponse: any;
    deviationRanges?: {
        alarmLow: number; // deviation < alarmLow => danger
        warningLow: number; // alarmLow <= deviation < warningLow => warning
        warningHigh: number; // warningLow <= deviation <= warningHigh => normal
        alarmHigh: number; // warningHigh < deviation <= alarmHigh => warning, deviation > alarmHigh => danger
    };
}

type DeviationItem = {
    key: string;
    actual: number | null;
    setpoint: number | null;
    deviation: number | null;
};

const toNumberOrNull = (value: any): number | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

const formatValue = (value: any, suffix = ''): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'True' : 'False';
    if (typeof value === 'number') return `${value}${suffix}`;
    return `${String(value)}${suffix}`;
};

export const RaptorCoatingControlDashboard: React.FC<RaptorCoatingControlDashboardProps> = ({
    section,
    apiResponse,
    deviationRanges,
}) => {
    const [view, setView] = React.useState<RaptorView>('dashboard');
    const [attentionOnly, setAttentionOnly] = React.useState<boolean>(false);
    const [showFlow, setShowFlow] = React.useState<boolean>(true);
    const [showCarrier, setShowCarrier] = React.useState<boolean>(true);
    const [showCMPPFlow, setShowCMPPFlow] = React.useState<boolean>(true);
    const [showCMPPCarrier, setShowCMPPCarrier] = React.useState<boolean>(true);

    const lastActuals = apiResponse?.lastActuals ?? {};
    const recipe = apiResponse?.lastSetpoint?.recipe ?? {};

    const deviationMap = React.useMemo(() => {
        const map = new Map<string, DeviationItem>();
        const deviations: any[] = Array.isArray(apiResponse?.deviations) ? apiResponse.deviations : [];
        deviations.forEach((d: any) => {
            if (d?.key) {
                map.set(String(d.key), {
                    key: String(d.key),
                    actual: toNumberOrNull(d.actual),
                    setpoint: toNumberOrNull(d.setpoint),
                    deviation: toNumberOrNull(d.deviation),
                });
            }
        });
        return map;
    }, [apiResponse?.deviations]);

    const getActual = (key: string, fallbackKeys: string[] = []) => {
        const candidates = [key, ...fallbackKeys];
        for (const k of candidates) {
            if (lastActuals && Object.prototype.hasOwnProperty.call(lastActuals, k)) {
                return lastActuals[k];
            }
        }
        return null;
    };

    const getDeviation = (key: string, actual: any, setpoint: any) => {
        const d = deviationMap.get(key);
        if (d?.deviation !== null && d?.deviation !== undefined) return d.deviation;
        const a = toNumberOrNull(actual);
        const s = toNumberOrNull(setpoint);
        if (a === null || s === null) return null;
        return s - a;
    };

    const Table = ({
        title,
        rows,
    }: {
        title: string;
        rows: Array<{ key: string; label: string; actual: any; setpoint: any }>;
    }) => (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900">{title}</h4>
            </div>

            <div className="overflow-auto max-h-96 rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parameter</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actual</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Set Point</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Deviation</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {rows.map((r) => {
                            const deviation = getDeviation(r.key, r.actual, r.setpoint);
                            return (
                                <tr key={r.key}>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.label}</td>
                                    <td className="px-4 py-3 text-sm text-right font-mono text-gray-900">
                                        {formatValue(r.actual)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right font-mono text-gray-600">
                                        {formatValue(r.setpoint)}
                                    </td>
                                    <td
                                        className={`px-4 py-3 text-sm text-right font-mono ${
                                            deviation === null
                                                ? 'text-gray-500'
                                                : deviation > 0
                                                    ? 'text-red-600'
                                                    : deviation < 0
                                                        ? 'text-blue-600'
                                                        : 'text-gray-700'
                                        }`}
                                    >
                                        {deviation !== null ? (deviation > 0 ? '+' : '') + deviation : 'N/A'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );

    // FBE mapping (Beds 1 & 2) + atomising / fluidising air for FBE beds
    const fbeRows = React.useMemo(() => {
        const bed1 = recipe?.fbeBedOneSP ?? {};
        const bed2 = recipe?.fbeBedTwoSP ?? {};

        const rows: Array<{ key: string; label: string; actual: any; setpoint: any }> = [];

        for (let i = 1; i <= 10; i++) {
            const bed = i <= 5 ? bed1 : bed2;
            const idx = i <= 5 ? i : i - 5;
            rows.push({
                key: `fbeFlow_${i}`,
                label: `FBE Flow ${i}`,
                actual: getActual(`fbeFlow_${i}`),
                setpoint: bed?.[`flowNozzleSp_${idx}`],
            });
        }

        for (let i = 1; i <= 10; i++) {
            const bed = i <= 5 ? bed1 : bed2;
            const idx = i <= 5 ? i : i - 5;
            rows.push({
                key: `fbeCarrier_${i}`,
                label: `FBE Carrier ${i}`,
                actual: getActual(`fbeCarrier_${i}`),
                setpoint: bed?.[`carrierNozzleSp_${idx}`],
            });
        }

        rows.push(
            {
                key: 'atomising1',
                label: 'Atomising Air 1 (FBE Bed 1)',
                actual: getActual('atomising1'),
                setpoint: bed1?.atomisingSp,
            },
            {
                key: 'atomising2',
                label: 'Atomising Air 2 (FBE Bed 2)',
                actual: getActual('atomising2'),
                setpoint: bed2?.atomisingSp,
            },
            {
                key: 'fbeFluidising_1',
                label: 'FBE Fluidising 1',
                actual: getActual('fbeFluidising_1'),
                setpoint: bed1?.fluidisingSp,
            },
            {
                key: 'fbeFluidising_2',
                label: 'FBE Fluidising 2',
                actual: getActual('fbeFluidising_2', ['fbefluidising_2']),
                setpoint: bed2?.fluidisingSp,
            }
        );

        return rows;
    }, [recipe, deviationMap, apiResponse?.lastActuals]);

    // CMPP mapping (Beds 1 & 2) + atomising / fluidising air for CMPP beds
    const cmppRows = React.useMemo(() => {
        const bed1 = recipe?.cmppBedOneSP ?? {};
        const bed2 = recipe?.cmppBedTwoSP ?? {};

        const rows: Array<{ key: string; label: string; actual: any; setpoint: any }> = [];

        for (let i = 1; i <= 10; i++) {
            const bed = i <= 5 ? bed1 : bed2;
            const idx = i <= 5 ? i : i - 5;
            const actualKey = `cmppFlow_${i}`;
            rows.push({
                key: actualKey,
                label: `CMPP Flow ${i}`,
                actual: getActual(actualKey, [`cmppflow_${i}`]),
                setpoint: bed?.[`flowNozzleSp_${idx}`],
            });
        }

        for (let i = 1; i <= 10; i++) {
            const bed = i <= 5 ? bed1 : bed2;
            const idx = i <= 5 ? i : i - 5;
            rows.push({
                key: `cmppCarrier_${i}`,
                label: `CMPP Carrier ${i}`,
                actual: getActual(`cmppCarrier_${i}`),
                setpoint: bed?.[`carrierNozzleSp_${idx}`],
            });
        }

        rows.push(
            {
                key: 'atomising3',
                label: 'Atomising Air 3 (CMPP Bed 1)',
                actual: getActual('atomising3'),
                setpoint: bed1?.atomisingSp,
            },
            {
                key: 'atomising4',
                label: 'Atomising Air 4 (CMPP Bed 2)',
                actual: getActual('atomising4'),
                setpoint: bed2?.atomisingSp,
            },
            {
                key: 'cmppFluidising_1',
                label: 'CMPP Fluidising 1',
                actual: getActual('cmppFluidising_1'),
                setpoint: bed1?.fluidisingSp,
            },
            {
                key: 'cmppFluidising_2',
                label: 'CMPP Fluidising 2',
                actual: getActual('cmppFluidising_2'),
                setpoint: bed2?.fluidisingSp,
            }
        );

        return rows;
    }, [recipe, deviationMap, apiResponse?.lastActuals]);

    const title = section === 'fbe' ? 'FBE Tables' : 'CMPP Tables';

    const subtitle = section === 'fbe' ? 'FBE Control' : 'CMPP Control';

    const activeRows = React.useMemo(() => {
        return section === 'fbe' ? fbeRows : cmppRows;
    }, [section, fbeRows, cmppRows]);

    const thresholds = React.useMemo(() => {
        // Safe defaults (domain-specific thresholds can be passed via props)
        const alarmLow = deviationRanges?.alarmLow ?? -0.5;
        const warningLow = deviationRanges?.warningLow ?? -0.1;
        const warningHigh = deviationRanges?.warningHigh ?? 0.1;
        const alarmHigh = deviationRanges?.alarmHigh ?? 0.5;
        return { alarmLow, warningLow, warningHigh, alarmHigh };
    }, [deviationRanges?.alarmLow, deviationRanges?.warningLow, deviationRanges?.warningHigh, deviationRanges?.alarmHigh]);

    const getStatus = React.useCallback(
        (deviation: number | null, actual: any) => {
            const actualNum = toNumberOrNull(actual);
            if (actual === null || actual === undefined || actualNum === null) return 'danger' as const;
            if (deviation === null) return 'danger' as const;
            // Normal: warningLow <= deviation <= warningHigh
            if (deviation >= thresholds.warningLow && deviation <= thresholds.warningHigh) return 'normal' as const;
            // Danger: deviation < alarmLow OR deviation > alarmHigh
            if (deviation < thresholds.alarmLow || deviation > thresholds.alarmHigh) return 'danger' as const;
            // Warning: between alarmLow..warningLow OR warningHigh..alarmHigh
            return 'warning' as const;
        },
        [thresholds.alarmLow, thresholds.warningLow, thresholds.warningHigh, thresholds.alarmHigh]
    );

    const summary = React.useMemo(() => {
        let normal = 0;
        let warning = 0;
        let danger = 0;
        let na = 0;
        let maxAbs = 0;

        activeRows.forEach((r) => {
            const deviation = getDeviation(r.key, r.actual, r.setpoint);
            const status = getStatus(deviation, r.actual);
            if (r.actual === null || r.actual === undefined) na++;
            if (deviation !== null) maxAbs = Math.max(maxAbs, Math.abs(deviation));
            if (status === 'normal') normal++;
            else if (status === 'warning') warning++;
            else danger++;
        });

        return { total: activeRows.length, normal, warning, danger, na, maxAbs };
    }, [activeRows, getStatus]);

    const attentionRows = React.useMemo(() => {
        let filtered = activeRows
            .map((r) => {
                const deviation = getDeviation(r.key, r.actual, r.setpoint);
                const status = getStatus(deviation, r.actual);
                if (status === 'normal') return null;
                return { ...r, deviation, status };
            })
            .filter(Boolean) as Array<any>;

        // Filter by Flow/Carrier for FBE section in dashboard view
        if (section === 'fbe' && view === 'dashboard') {
            filtered = filtered.filter((r) => {
                const isFlow = r.key.startsWith('fbeFlow_');
                const isCarrier = r.key.startsWith('fbeCarrier_');
                if (isFlow) return showFlow;
                if (isCarrier) return showCarrier;
                return true; // Keep other types if any
            });
        }

        // Filter by Flow/Carrier for CMPP section in dashboard view
        if (section === 'cmpp' && view === 'dashboard') {
            filtered = filtered.filter((r) => {
                const isFlow = r.key.startsWith('cmppFlow_');
                const isCarrier = r.key.startsWith('cmppCarrier_');
                if (isFlow) return showCMPPFlow;
                if (isCarrier) return showCMPPCarrier;
                return true; // Keep other types if any
            });
        }

        return filtered;
    }, [activeRows, getStatus, section, view, showFlow, showCarrier, showCMPPFlow, showCMPPCarrier]);

    // If user switches away from the FBE/CMPP dashboard, reset the expanded attention panel.
    React.useEffect(() => {
        if ((section !== 'fbe' && section !== 'cmpp') || view !== 'dashboard') {
            setAttentionOnly(false);
        }
    }, [section, view]);

    return (
        <div className="mt-6 bg-white rounded-lg border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                            <Layers className="h-5 w-5 text-indigo-500" />
                            {subtitle}
                            <span className="text-sm font-normal text-gray-500">
                                ({apiResponse?.lastActuals?.id ?? '-'})
                            </span>
                        </h3>
                        {section === 'fbe' && view === 'dashboard' && (
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="show-flow"
                                        checked={showFlow}
                                        onCheckedChange={(checked) => setShowFlow(checked === true)}
                                    />
                                    <Label htmlFor="show-flow" className="text-sm text-gray-700 cursor-pointer">
                                        Flow
                                    </Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="show-carrier"
                                        checked={showCarrier}
                                        onCheckedChange={(checked) => setShowCarrier(checked === true)}
                                    />
                                    <Label htmlFor="show-carrier" className="text-sm text-gray-700 cursor-pointer">
                                        Carrier
                                    </Label>
                                </div>
                            </div>
                        )}
                        {section === 'cmpp' && view === 'dashboard' && (
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="show-cmpp-flow"
                                        checked={showCMPPFlow}
                                        onCheckedChange={(checked) => setShowCMPPFlow(checked === true)}
                                    />
                                    <Label htmlFor="show-cmpp-flow" className="text-sm text-gray-700 cursor-pointer">
                                        Flow
                                    </Label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="show-cmpp-carrier"
                                        checked={showCMPPCarrier}
                                        onCheckedChange={(checked) => setShowCMPPCarrier(checked === true)}
                                    />
                                    <Label htmlFor="show-cmpp-carrier" className="text-sm text-gray-700 cursor-pointer">
                                        Carrier
                                    </Label>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant={view === 'dashboard' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setView('dashboard')}
                            className="text-xs"
                        >
                            Dashboard
                        </Button>
                        <Button
                            variant={view === 'grid' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setView('grid')}
                            className="text-xs"
                        >
                            Grid
                        </Button>
                        <Button
                            variant={view === 'table' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setView('table')}
                            className="text-xs"
                        >
                            Table
                        </Button>
                        <Button
                            variant={view === 'warnings' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setView('warnings')}
                            className="text-xs"
                        >
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Warnings
                        </Button>
                        <Button
                            variant={view === 'danger' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setView('danger')}
                            className="text-xs bg-red-50 hover:bg-red-100"
                        >
                            <AlertTriangle className="h-3 w-3 mr-1 text-red-600" />
                            Danger
                        </Button>
                    </div>
                </div>
            </div>

            <div className="p-6 space-y-8">
                {view === 'dashboard' && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                                <div className="text-2xl font-bold text-green-700">{summary.normal}</div>
                                <div className="flex items-center justify-center gap-1 mt-2">
                                    <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                                    <div className="text-xs text-green-600 font-medium">Normal</div>
                                </div>
                            </div>
                            <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                <div className="text-2xl font-bold text-yellow-700">{summary.warning}</div>
                                <div className="flex items-center justify-center gap-1 mt-2">
                                    <AlertTriangle className="h-3 w-3 text-yellow-600" />
                                    <div className="text-xs text-yellow-600 font-medium">Warnings</div>
                                </div>
                            </div>
                            <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                                <div className="text-2xl font-bold text-red-700">{summary.danger}</div>
                                <div className="flex items-center justify-center gap-1 mt-2">
                                    <AlertTriangle className="h-3 w-3 text-red-600" />
                                    <div className="text-xs text-red-600 font-medium">Danger</div>
                                </div>
                            </div>
                            <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="text-lg font-bold text-blue-700">{summary.total}</div>
                                <div className="flex items-center justify-center gap-1 mt-2">
                                    <div className="text-xs text-blue-600 font-medium">Parameters</div>
                                </div>
                            </div>
                            <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                                <div className="text-lg font-bold text-purple-700">{summary.maxAbs}</div>
                                <div className="flex items-center justify-center gap-1 mt-2">
                                    <div className="text-xs text-purple-600 font-medium">Max |Deviation|</div>
                                </div>
                            </div>
                        </div>

                        {/* Parameters Requiring Attention (Warnings + Danger) */}
                        {attentionRows.length > 0 && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                                    <h4 className="text-sm font-medium text-yellow-800">Parameters Requiring Attention:</h4>
                                </div>

                                {attentionOnly ? (
                                    <>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="text-sm text-gray-700">
                                                Showing {attentionRows.length} parameters requiring attention.
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setAttentionOnly(false)}
                                                className="text-xs font-medium text-blue-700 underline underline-offset-2 hover:text-blue-800"
                                            >
                                                Show less
                                            </button>
                                        </div>
                                        <div className="space-y-1 max-h-[420px] overflow-auto pr-1">
                                            {attentionRows.map((r) => (
                                                <div
                                                    key={r.key}
                                                    className={`text-sm ${
                                                        r.status === 'danger'
                                                            ? 'text-red-700 font-semibold'
                                                            : 'text-gray-700'
                                                    }`}
                                                >
                                                    <span className="font-medium">{r.label}:</span> {formatValue(r.actual)}
                                                    <span className={r.status === 'danger' ? 'text-red-700' : 'text-yellow-700'}>
                                                        {' '}
                                                        ({r.deviation !== null ? (r.deviation > 0 ? '+' : '') + r.deviation : 'N/A'} deviation)
                                                        {r.status === 'danger' && ' ⚠️ DANGER'}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-sm text-gray-700">
                                        {attentionRows.length} parameters require attention.{' '}
                                        <button
                                            type="button"
                                            onClick={() => setAttentionOnly(true)}
                                            className="font-medium text-blue-700 underline underline-offset-2 hover:text-blue-800"
                                        >
                                            Show more
                                        </button>{' '}
                                        to view details.
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {view === 'grid' && (
                    <div className="space-y-4">
                        <div className="text-center text-sm text-gray-500 mb-4">{title} Grid</div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-10 gap-2">
                            {activeRows.map((r) => {
                                const deviation = getDeviation(r.key, r.actual, r.setpoint);
                                const status = getStatus(deviation, r.actual);
                                return (
                                    <div key={r.key} className="text-center">
                                        <div
                                            className={`p-2 rounded text-xs font-mono border-2 ${
                                                status === 'danger'
                                                    ? 'border-red-500 bg-red-100 text-red-800'
                                                    : status === 'warning'
                                                        ? 'border-yellow-400 bg-yellow-100 text-yellow-800'
                                                        : 'border-green-400 bg-green-100 text-green-800'
                                            }`}
                                            title={`${r.label}\nActual: ${formatValue(r.actual)}\nSP: ${formatValue(
                                                r.setpoint
                                            )}\nDev: ${deviation !== null ? deviation : 'N/A'}`}
                                        >
                                            <div className="font-bold truncate" title={r.label}>
                                                {r.label}
                                            </div>
                                            <div className="text-xs">{formatValue(r.actual)}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {view === 'table' && <Table title={title} rows={activeRows} />}

                {view === 'warnings' && (() => {
                    const warningRows = activeRows
                        .map((r) => {
                            const deviation = getDeviation(r.key, r.actual, r.setpoint);
                            const status = getStatus(deviation, r.actual);
                            if (status === 'normal') return null;
                            return { ...r, deviation, status };
                        })
                        .filter(Boolean) as Array<any>;

                    if (warningRows.length === 0) {
                        return (
                            <div className="text-center py-8 text-green-600">
                                ✅ All parameters operating normally
                            </div>
                        );
                    }

                    return (
                        <div className="space-y-3">
                            <h4 className="text-sm font-medium text-yellow-800 mb-3">⚠️ Parameters with Warnings:</h4>
                            {warningRows.map((r) => (
                                <div
                                    key={r.key}
                                    className={`p-3 rounded-lg border-2 ${
                                        r.status === 'danger'
                                            ? 'border-red-400 bg-red-50'
                                            : 'border-yellow-300 bg-yellow-50'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div
                                                className={`font-medium ${
                                                    r.status === 'danger' ? 'text-red-800' : 'text-yellow-800'
                                                }`}
                                            >
                                                {r.label} {r.status === 'danger' && '⚠️ DANGER'}
                                            </div>
                                            <div
                                                className={`text-sm ${
                                                    r.status === 'danger' ? 'text-red-600' : 'text-yellow-600'
                                                }`}
                                            >
                                                Current: {formatValue(r.actual)} | Target: {formatValue(r.setpoint)}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div
                                                className={`text-lg font-bold ${
                                                    r.status === 'danger' ? 'text-red-700' : 'text-yellow-700'
                                                }`}
                                            >
                                                {r.deviation !== null ? (r.deviation > 0 ? '+' : '') + r.deviation : 'N/A'}
                                            </div>
                                            <div
                                                className={`text-xs ${
                                                    r.status === 'danger' ? 'text-red-600' : 'text-yellow-600'
                                                }`}
                                            >
                                                Deviation
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    );
                })()}

                {view === 'danger' && (() => {
                    const dangerRows = activeRows
                        .map((r) => {
                            const deviation = getDeviation(r.key, r.actual, r.setpoint);
                            const status = getStatus(deviation, r.actual);
                            if (status !== 'danger') return null;
                            return { ...r, deviation };
                        })
                        .filter(Boolean) as Array<any>;

                    if (dangerRows.length === 0) {
                        return <div className="text-center py-8 text-green-600">✅ No parameters in danger</div>;
                    }

                    return (
                        <div className="space-y-3">
                            <h4 className="text-sm font-medium text-red-800 mb-3">🔴 Parameters in Danger:</h4>
                            {dangerRows.map((r) => (
                                <div key={r.key} className="p-3 rounded-lg border-2 border-red-400 bg-red-50">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="font-medium text-red-800">{r.label} ⚠️ DANGER</div>
                                            <div className="text-sm text-red-600">
                                                Current: {formatValue(r.actual)} | Target: {formatValue(r.setpoint)}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-lg font-bold text-red-700">
                                                {r.deviation !== null ? (r.deviation > 0 ? '+' : '') + r.deviation : 'N/A'}
                                            </div>
                                            <div className="text-xs text-red-600">Deviation</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    );
                })()}
            </div>
        </div>
    );
};

